import React, {Fragment, useEffect, useState} from 'react';
import {FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import Formatter from '../../../util/formatter';
import {user} from '../../../store/reducers/user';
import Notes from '../../../util/notes';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {useTranslation} from 'react-i18next';
import Typography from '@mui/material/Typography';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';

const useStyles = makeStyles((theme) => ({

    divider: {
        width: '100%',
        margin: '5px',
    },

    blockSwitch: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    textField: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },

    provideDetailsLabel: {
        alignItems: 'center',
    },

    radio: {
        width: '100%',
        minWidth: '100%',
    },

    mandatory: {
        fontWeight: 'bold',
        color: '#e06666',
    },
    clearButton: {
        border: '1px solid #2180f7',
        height: '25px',
        placeSelf: 'center',
    },
    container: {
        padding: '15px',
    },
    title: {
        marginBottom: '24px',
        fontWeight: 600,
        textAlign: 'center',
    },
}));

const FallsAmbForm = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    // Selectors
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const [fallsAmbForm, setFallsAmbForm] = useState({
        clinicalFrailty: '',
        typeOfFall: '', //dropdown
        timeFallOccurred: null, 
        fallHistory: false,
        timeOnFloor: '',
        ableToMove: false,
        urineOutput: false,
        fluidIntake: false,
        bloodPressure: '',
    });

    useEffect(() => {
        if (props.patient?.studies?.fallsAmbForm) {
            setFallsAmbForm(props.patient?.studies?.fallsAmbForm);
        }
    }, []);


    useEffect(() => {
        if (props.onDisableSubmit) {
            props.onDisableSubmit(isSubmitDisabled())
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSaveForm);
        }
    }, [fallsAmbForm, userObject]);

    const handleChangeRadio = (event) => {
        const {name, value} = event.target;
        setFallsAmbForm(pr => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleChangeTextField = (event) => {
        const {name, value} = event.target;
        setFallsAmbForm(pr => ({
            ...pr,
            [name]: value,
        }));
    };

    const handleChangeBlockSwitch = (name) => {
        setFallsAmbForm(pr => ({
            ...pr,
            [name]: !pr[name],
        }));
    };

    const isSubmitDisabled = () => {
        return Object.values(fallsAmbForm).some(
            value => value == null || value === ''
        );
    };

    const handleSaveForm = () => {
        const objectToSave = {
            ...fallsAmbForm,
            timeStamp: moment().toISOString(),
            by: tester,
        };
        let dataArray = [{
            name: 'studies.fallsAmbForm',
            value: objectToSave,
        }];

        props.onChange(dataArray);
    };

    return (
        <>
        <Typography variant="h5" className={classes.title}>
            Falls Assessment
        </Typography>

        <Grid container spacing={2} className={classes.container}>
            <Grid item xs={2.5}>
                <FormLabel className={classes.label}>
                    {t('Clinical frailty')}
                </FormLabel>
            </Grid>
            <Grid item xs={3.5}>
                <AgyleTextField
                    fullWidth
                    name="clinicalFrailty"
                    maxRows={25}
                    multiline
                    onChange={handleChangeTextField}
                    value={fallsAmbForm.clinicalFrailty}
                    variant="outlined"
                    error={!fallsAmbForm.clinicalFrailty}
                    noBorder={false}
                    withNewBackground
                />
            </Grid>
            <Grid item xs={0.5}/>
            <Grid item xs={2}>
                <FormLabel className={classes.label}>
                    {t('Type of fall')}
                </FormLabel>
            </Grid>
            {/*dropdown*/}
            <Grid item xs={3.5}>
                <AgyleTextField
                    fullWidth
                    name="typeOfFall"
                    maxRows={25}
                    multiline
                    onChange={handleChangeTextField}
                    value={fallsAmbForm.typeOfFall}
                    variant="outlined"
                    error={!fallsAmbForm.typeOfFall}
                    noBorder={false}
                    withNewBackground
                />
            </Grid>

            <Grid item xs={2.5}>
                <FormLabel className={classes.label}>
                    {t('Time fall occurred')}
                </FormLabel>
            </Grid>
            <Grid item xs={3.5}>
                <AgyleDatePicker
                    type="dateTime"
                    error={!fallsAmbForm.timeFallOccurred}
                    value={fallsAmbForm.timeFallOccurred}
                    onChange={(param) => {setFallsAmbForm((pr) =>
                        ({
                            ...pr,
                            timeFallOccurred: param
                        }))}}
                    noBorder={false}
                    withNewBackground
                    maxDate={moment()}
                />
            </Grid>
            <Grid item xs={0.5}/>
            <Grid item xs={5.5}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                >
                    <Grid item xs={6.5}>
                        <FormLabel>
                            {t('Fall history')}
                        </FormLabel>
                    </Grid>
                    <Grid item xs={5.5} className={classes.blockSwitch}>
                        <AgyleBlockSwitch
                            value={fallsAmbForm.fallHistory}
                            onChange={() => handleChangeBlockSwitch('fallHistory')}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={2.5}>
                <FormLabel className={classes.label}>
                    {t('Time on floor')}
                </FormLabel>
            </Grid>
            <Grid item xs={3.5}>
                <AgyleTextField
                    fullWidth
                    name="timeOnFloor"
                    type="number"
                    maxRows={25}
                    multiline
                    onChange={handleChangeTextField}
                    value={fallsAmbForm.timeOnFloor}
                    variant="outlined"
                    error={!fallsAmbForm.timeOnFloor}
                    noBorder={false}
                    withNewBackground
                />
            </Grid>
            <Grid item xs={0.5}/>
            <Grid item xs={5.5}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                >
                    <Grid item xs={6.5}>
                        <FormLabel>
                            {t('Patient able to move on floor')}
                        </FormLabel>
                    </Grid>
                    <Grid item xs={5.5} className={classes.blockSwitch}>
                        <AgyleBlockSwitch
                            value={fallsAmbForm.ableToMove}
                            onChange={() => handleChangeBlockSwitch('ableToMove')}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={6}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                >
                    <Grid item xs={5}>
                        <FormLabel>
                            {t('Urine output')}
                        </FormLabel>
                    </Grid>
                    <Grid item xs={7} className={classes.blockSwitch}>
                        <AgyleBlockSwitch
                            value={fallsAmbForm.urineOutput}
                            onChange={() => handleChangeBlockSwitch('urineOutput')}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={0.5}/>

            <Grid item xs={5.5}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                >
                    <Grid item xs={6.5}>
                        <FormLabel>
                            {t('Fluid intake')}
                        </FormLabel>
                    </Grid>
                    <Grid item xs={5.5} className={classes.blockSwitch}>
                        <AgyleBlockSwitch
                            value={fallsAmbForm.fluidIntake}
                            onChange={() => handleChangeBlockSwitch('fluidIntake')}
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={2.5}>
                <FormLabel className={classes.label}>
                    {t('Blood Pressure')}
                </FormLabel>
            </Grid>
            <Grid item xs={3.5}>
                <AgyleTextField
                    fullWidth
                    name="bloodPressure"
                    maxRows={25}
                    multiline
                    onChange={handleChangeTextField}
                    value={fallsAmbForm.bloodPressure}
                    variant="outlined"
                    error={!fallsAmbForm.bloodPressure}
                    noBorder={false}
                    withNewBackground
                />
            </Grid>
        </Grid>
        </>
    );
};

export default FallsAmbForm;